import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutGrid, Plus, Search, Pin, Star, Trash2, Clock, X, BarChart2, Sparkles, Cloud, TrendingUp, Zap } from 'lucide-react';
import { useWorkspaces, type Workspace, type Widget } from '../hooks/useWorkspaces';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { cn } from '../components/ui/Button';
const TEMPLATES: {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  widgets: Widget[];
  counter: number;
}[] = [{
  id: 'meteorologist',
  name: 'Meteorologist',
  description: 'Weather analysis with maps, charts, and station data',
  icon: <Cloud size={18} className="text-[#EC4899]" data-id="element-4240" />,
  counter: 5,
  widgets: [{
    i: 'map-1',
    x: 0,
    y: 0,
    w: 8,
    h: 6,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }, {
    i: 'chart-1',
    x: 8,
    y: 0,
    w: 4,
    h: 3,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 8,
    y: 3,
    w: 4,
    h: 3,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 6,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 6,
    w: 6,
    h: 4,
    type: 'chart'
  }]
}, {
  id: 'short-term-trader',
  name: 'Short Term Trader',
  description: 'Intraday price signals, demand forecasts, and model runs',
  icon: <Zap size={18} className="text-[#EC4899]" data-id="element-4241" />,
  counter: 5,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 4,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 4,
    y: 0,
    w: 8,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 4,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 12,
    h: 5,
    type: 'map'
  }]
}, {
  id: 'energy-analyst',
  name: 'Energy Analyst',
  description: 'Long-range forecasts, model comparisons, and reporting',
  icon: <BarChart2 size={18} className="text-[#EC4899]" data-id="element-4242" />,
  counter: 5,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 6,
    y: 0,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 8,
    h: 4,
    type: 'table'
  }, {
    i: 'text-1',
    x: 8,
    y: 4,
    w: 4,
    h: 4,
    type: 'text',
    config: {
      prompt: 'Add your analysis notes here...'
    }
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 12,
    h: 5,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }]
}, {
  id: 'energy-trader',
  name: 'Energy Trader',
  description: 'Multi-market price monitoring, generation mix, and live positions',
  icon: <TrendingUp size={18} className="text-[#EC4899]" data-id="element-4243" />,
  counter: 7,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 5,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 5,
    y: 0,
    w: 7,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 4,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 8,
    h: 5,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }, {
    i: 'text-1',
    x: 8,
    y: 8,
    w: 4,
    h: 5,
    type: 'text',
    config: {
      prompt: 'Log positions and market observations here...'
    }
  }, {
    i: 'chart-4',
    x: 0,
    y: 13,
    w: 12,
    h: 4,
    type: 'chart'
  }]
}];
export function WorkspacesOverviewView() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    workspaces,
    createWorkspace,
    deleteWorkspace,
    togglePin,
    toggleFavorite,
    setActiveWorkspace,
    updateWidgets,
    updateWidgetsById,
    renameWorkspace
  } = useWorkspaces();
  const [filter, setFilter] = useState<'all' | 'pinned' | 'favorites'>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<'choose' | 'template'>('choose');
  // Auto-open modal when navigated here with openModal state
  useEffect(() => {
    if ((location.state as any)?.openModal) {
      setModalStep('choose');
      setModalOpen(true);
      // Clear the state so refreshing doesn't re-open
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  const filteredWorkspaces = workspaces.filter(w => {
    if (filter === 'pinned' && !w.pinned) return false;
    if (filter === 'favorites' && !w.favorited) return false;
    return true;
  }).sort((a, b) => {
    // Pinned first, then updated recently
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
  const handleCreate = () => {
    setModalStep('choose');
    setModalOpen(true);
  };
  const handleNewBlank = () => {
    setModalOpen(false);
    const newId = createWorkspace();
    navigate(`/workspace/${newId}`, {
      state: {
        renaming: true,
        openAiPanel: true
      }
    });
  };
  const handleUseTemplate = (template: (typeof TEMPLATES)[number]) => {
    setModalOpen(false);
    // Short Term Trader → pin the Intraday Trader page to the sidebar and navigate there
    if (template.id === 'short-term-trader') {
      try {
        const existing = JSON.parse(localStorage.getItem('jua-pinned-pages') || '[]');
        if (!existing.includes('intraday')) {
          localStorage.setItem('jua-pinned-pages', JSON.stringify([...existing, 'intraday']));
        }
      } catch {}
      navigate('/intraday');
      return;
    }
    // Generate a unique name: "Meteorologist", "Meteorologist 2", "Meteorologist 3", etc.
    const baseName = template.name;
    const existingNames = new Set(workspaces.map(w => w.name));
    let uniqueName = baseName;
    let suffix = 2;
    while (existingNames.has(uniqueName)) {
      uniqueName = `${baseName} ${suffix}`;
      suffix++;
    }
    const newId = createWorkspace();
    setTimeout(() => {
      updateWidgetsById(newId, template.widgets, template.counter);
      renameWorkspace(newId, uniqueName);
      navigate(`/workspace/${newId}`, {
        state: {
          openAiPanel: true
        }
      });
    }, 0);
  };
  const handleCardClick = (id: string) => {
    setActiveWorkspace(id);
    navigate(`/workspace/${id}`);
  };
  return <div className="jua-workspaces-overview jua-page-bg min-h-screen text-slate-100 p-6 md:p-8" data-id="element-4244">
      <div className="max-w-7xl mx-auto space-y-8" data-id="element-4245">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-4246">
          <div data-id="element-4247">
            <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-4248">
              <LayoutGrid className="text-[#EC4899]" data-id="element-4249" />
              Workspaces
            </h1>
            <p className="text-slate-400 mt-1" data-id="element-4250">
              Manage and organize your trading dashboards
            </p>
          </div>
          <Button onClick={handleCreate} className="gap-2" data-id="element-4251">
            <Plus size={16} data-id="element-4252" />
            New Workspace
          </Button>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between" data-id="element-4253">
          <div className="jua-ws-filter-tabs flex items-center gap-1 p-1.5 rounded-full bg-white/[0.04] border border-white/[0.14] backdrop-blur-2xl w-fit" style={{
          WebkitBackdropFilter: 'blur(24px) saturate(150%)',
          backdropFilter: 'blur(24px) saturate(150%)'
        }} data-id="element-4254">
            {(['all', 'pinned', 'favorites'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={cn('px-6 py-2 text-sm font-medium transition-all duration-200 capitalize rounded-full', filter === f ? 'jua-ws-tab-active bg-white/[0.10] text-white shadow-[0_1px_4px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.08)]' : 'text-slate-400 hover:text-slate-200')} data-id="element-4255">
                {f}
              </button>)}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-4256">
          {filteredWorkspaces.map(ws => <div key={ws.id} onClick={() => handleCardClick(ws.id)} className="jua-ws-card group relative flex flex-col bg-slate-800/40 hover:bg-slate-800/80 border border-slate-700/50 hover:border-slate-600 transition-all rounded-xl p-5 cursor-pointer h-[200px]" data-id="element-4257">
              {/* Top Actions */}
              <div className="flex items-start justify-between mb-3" data-id="element-4258">
                <div className="w-9 h-9 rounded-lg bg-[#EC4899]/10 border border-[#EC4899]/20 flex items-center justify-center text-[#EC4899]" data-id="element-4259">
                  <LayoutGrid size={16} data-id="element-4260" />
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4261">
                  <button onClick={e => {
                e.stopPropagation();
                togglePin(ws.id);
              }} className={cn('p-1.5 rounded-md transition-colors', ws.pinned ? 'text-[#EC4899] bg-[#EC4899]/10' : 'text-slate-400 hover:text-white hover:bg-white/[0.1]')} title={ws.pinned ? 'Unpin' : 'Pin'} data-id="element-4262">
                    <Pin size={14} className={ws.pinned ? 'fill-current' : ''} data-id="element-4263" />
                  </button>
                  <button onClick={e => {
                e.stopPropagation();
                toggleFavorite(ws.id);
              }} className={cn('p-1.5 rounded-md transition-colors', ws.favorited ? 'text-amber-400 bg-amber-400/10' : 'text-slate-400 hover:text-white hover:bg-white/[0.1]')} title={ws.favorited ? 'Unfavorite' : 'Favorite'} data-id="element-4264">
                    <Star size={14} className={ws.favorited ? 'fill-current' : ''} data-id="element-4265" />
                  </button>
                  <button onClick={e => {
                e.stopPropagation();
                if (confirm('Are you sure you want to delete this workspace?')) {
                  deleteWorkspace(ws.id);
                }
              }} className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Delete" data-id="element-4266">
                    <Trash2 size={14} data-id="element-4267" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 overflow-hidden" data-id="element-4268">
                <div className="flex items-center gap-2 mb-1" data-id="element-4269">
                  <h3 className="text-sm font-semibold text-white truncate group-hover:text-[#EC4899] transition-colors" data-id="element-4270">
                    {ws.name}
                  </h3>
                  {ws.pinned && !ws.favorited && null}
                  {ws.favorited && <Star size={11} className="text-amber-400 fill-amber-400 flex-shrink-0" data-id="element-4271" />}
                </div>
                <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed" data-id="element-4272">
                  {ws.description || 'No description'}
                </p>
              </div>

              {/* Footer */}
              <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-slate-500" data-id="element-4273">
                <div className="flex items-center gap-1.5" data-id="element-4274">
                  <LayoutGrid size={12} data-id="element-4275" />
                  <span data-id="element-4276">
                    {ws.widgets.length} widget
                    {ws.widgets.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-4277">
                  <Clock size={12} data-id="element-4278" />
                  <span data-id="element-4279">
                    {new Date(ws.updatedAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric'
                })}
                  </span>
                </div>
              </div>
            </div>)}

          {/* New Workspace Card */}
          <button onClick={handleCreate} className="jua-ws-new-card flex flex-col items-center justify-center gap-3 bg-slate-800/20 hover:bg-slate-800/40 border-2 border-dashed border-slate-700/50 hover:border-[#EC4899]/40 rounded-xl p-5 cursor-pointer h-[200px] transition-all group" data-id="element-4280">
            <div className="w-11 h-11 rounded-xl bg-slate-800/60 group-hover:bg-[#EC4899]/10 border border-white/[0.06] group-hover:border-[#EC4899]/20 flex items-center justify-center transition-all shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]" data-id="element-4281">
              <Plus size={20} className="text-slate-500 group-hover:text-[#EC4899] transition-colors" data-id="element-4282" />
            </div>
            <span className="text-sm font-medium text-slate-400 group-hover:text-white" data-id="element-4283">
              Create New Workspace
            </span>
          </button>
        </div>

        {filteredWorkspaces.length === 0 && <div className="text-center py-20" data-id="element-4284">
            <div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4285">
              <Search size={24} className="text-slate-500" data-id="element-4286" />
            </div>
            <h3 className="text-lg font-medium text-white mb-1" data-id="element-4287">
              No workspaces found
            </h3>
            <p className="text-slate-400" data-id="element-4288">
              Try adjusting your search or filters
            </p>
          </div>}
      </div>

      {/* New Workspace Modal */}
      {modalOpen && <div className="jua-ws-modal-overlay fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setModalOpen(false)} data-id="element-4289">
          <div className="jua-ws-modal w-full max-w-lg rounded-2xl border border-white/[0.08] shadow-2xl shadow-black/60 overflow-hidden" style={{
        background: 'rgba(15, 23, 42, 0.97)',
        backdropFilter: 'blur(40px)'
      }} onClick={e => e.stopPropagation()} data-id="element-4290">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]" data-id="element-4291">
              <div data-id="element-4292">
                <h2 className="text-base font-semibold text-white" data-id="element-4293">
                  New Workspace
                </h2>
                <p className="text-xs text-slate-400 mt-0.5" data-id="element-4294">
                  Start blank or pick a template
                </p>
              </div>
              <button onClick={() => setModalOpen(false)} className="p-1.5 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-colors" data-id="element-4295">
                <X size={16} data-id="element-4296" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5" data-id="element-4297">
              {/* Template Cards */}
              <div className="grid grid-cols-2 gap-3" data-id="element-4298">
                {TEMPLATES.map(tpl => <button key={tpl.id} onClick={() => handleUseTemplate(tpl)} className="flex flex-col items-center gap-3 p-5 rounded-xl border border-white/[0.08] hover:border-[#EC4899]/40 bg-white/[0.03] hover:bg-[#EC4899]/[0.06] transition-all group text-center" data-id="element-4299">
                    <div className="w-12 h-12 rounded-xl bg-slate-800 group-hover:bg-[#EC4899]/10 border border-white/[0.06] group-hover:border-[#EC4899]/20 flex items-center justify-center transition-all" data-id="element-4300">
                      {tpl.icon}
                    </div>
                    <div data-id="element-4301">
                      <div className="text-sm font-semibold text-white group-hover:text-[#EC4899] transition-colors mb-1" data-id="element-4302">
                        {tpl.name}
                      </div>
                      <div className="text-[11px] text-slate-400 leading-relaxed" data-id="element-4303">
                        {tpl.description}
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-auto" data-id="element-4304">
                      {tpl.widgets.length} widget
                      {tpl.widgets.length !== 1 ? 's' : ''}
                    </div>
                  </button>)}
              </div>

              {/* Blank — full width, separated */}
              <button onClick={handleNewBlank} className="w-full flex items-center gap-3 mt-3 px-4 py-3 rounded-xl border border-dashed border-white/[0.1] hover:border-[#EC4899]/40 bg-white/[0.02] hover:bg-[#EC4899]/[0.04] transition-all group" data-id="element-4305">
                <div className="w-9 h-9 rounded-lg bg-slate-800 group-hover:bg-[#EC4899]/10 border border-white/[0.06] group-hover:border-[#EC4899]/20 flex items-center justify-center transition-all flex-shrink-0" data-id="element-4306">
                  <Plus size={18} className="text-slate-400 group-hover:text-[#EC4899] transition-colors" data-id="element-4307" />
                </div>
                <div className="text-left" data-id="element-4308">
                  <div className="text-sm font-semibold text-white group-hover:text-[#EC4899] transition-colors" data-id="element-4309">
                    Blank workspace
                  </div>
                  <div className="text-[11px] text-slate-400" data-id="element-4310">
                    Start from scratch with an empty canvas
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>}
    </div>;
}